import { FC } from 'react';
import { useSelector } from 'react-redux';
import { TMinerData } from 'store/api/cuverse-api/purchase/types/purchase-active-miners.interface';
import { selectProfile } from 'store/profile-reducer/profile.selectors';
import { selectBtcUsdRate } from 'store/rates-reducer/rates.selectors';

import { AddToCartButton } from 'components/logic/AddToCartButton';
import { formatUsdOutput } from 'utils/formatters/format-usd-output.util';

import * as S from './MinerCard.styled';

interface MinerCardProps {
  minerData: TMinerData;
  t: (key: string) => string;
}

export const MinerCard: FC<MinerCardProps> = ({ minerData, t }) => {
  const {
    dailyElectricityFee,
    picture,
    model,
    hashrate,
    unitSymbol,
    price,
    powerConsumption,
    estDailyOutput,
    id,
    soldout,
  } = minerData;
  const { hasTrial } = useSelector(selectProfile);
  const btcUsdRate = useSelector(selectBtcUsdRate);
  const isTrialMiner = model.toLowerCase().includes('trial');
  const wattPerTh = (powerConsumption * 1000) / hashrate;

  //ROI calculation
  const DAYS_IN_YEAR = 365;
  const DISCOUNT_20 = 0.2;

  const feeWithDiscount = dailyElectricityFee - dailyElectricityFee * DISCOUNT_20;
  const dailyNetProfit = estDailyOutput * btcUsdRate - feeWithDiscount;
  const roi = (((dailyNetProfit * DAYS_IN_YEAR) / price) * 100).toFixed(1);

  const AVERAGE_DAYS_IN_MONTH = 30.5;
  const netProfitMonthly = dailyNetProfit * AVERAGE_DAYS_IN_MONTH;

  if (netProfitMonthly <= 0 || Number(roi) <= 0) {
    return null;
  }

  if (isTrialMiner && hasTrial) {
    return null;
  }

  return (
    <S.MinerItem as="li">
      <S.MinerCardHeader as="header">
        <S.MinerPicture>
          <source srcSet={`${picture}, ${picture} 2x, ${picture} 3x`} type="image/webp" />
          <source srcSet={`${picture}, ${picture} 2x, ${picture} 3x`} type="image/png" />
          <img width={80} height={80} src={picture} alt="Miner" />
        </S.MinerPicture>
        <S.MinerHeaderTextContent as="dl">
          <S.MinerName as="dt">{model.split('-')[0]}</S.MinerName>
          <S.MinerSpecifications as="dd">
            <S.SpecColumn>
              <S.SpecTittle>{t('purchase.hashRate')}</S.SpecTittle>
              <S.DescriptionValue>
                {hashrate} {unitSymbol}
              </S.DescriptionValue>
            </S.SpecColumn>
            <S.SpecColumn>
              <S.SpecTittle>{t('purchase.efficiency')}</S.SpecTittle>
              <S.DescriptionValue>{formatUsdOutput(wattPerTh)} W/TH</S.DescriptionValue>
            </S.SpecColumn>
          </S.MinerSpecifications>
        </S.MinerHeaderTextContent>
      </S.MinerCardHeader>
      <S.MinerCardRevenueContent as="section">
        <S.SpecRow as="dl">
          <S.SpecTittle as="dt">{t('purchase.monthlyProfit')}</S.SpecTittle>
          <S.SpecRowDescription as="dd">
            <S.DescriptionText>{t('purchase.upTo')}</S.DescriptionText>
            <S.DescriptionRowValue>${formatUsdOutput(netProfitMonthly)}</S.DescriptionRowValue>
          </S.SpecRowDescription>
        </S.SpecRow>
        <S.SpecRow as="dl">
          <S.SpecTittle as="dt">{t('purchase.annualRoi')}</S.SpecTittle>
          <S.SpecRowDescription as="dd">
            <S.DescriptionText>{t('purchase.upTo')}</S.DescriptionText>
            <S.DescriptionRowValue>{formatUsdOutput(roi)}%</S.DescriptionRowValue>
          </S.SpecRowDescription>
        </S.SpecRow>
        <S.SpecHr />
        <S.SpecRow as="dl">
          <S.SpecTittle as="dt">{t('purchase.minerPrice')}</S.SpecTittle>
          <S.DescriptionRowValue as="dd">${formatUsdOutput(price)}</S.DescriptionRowValue>
        </S.SpecRow>
      </S.MinerCardRevenueContent>
      <S.ButtonsWrapper as="footer">
        <AddToCartButton
          id={id}
          isSoldOut={!!soldout}
          t={t}
          isTrial={isTrialMiner}
          disabled={isTrialMiner && hasTrial}
        />
      </S.ButtonsWrapper>
    </S.MinerItem>
  );
};
